import { useState, useEffect } from "react";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { KeyRound, User, MapPin, Undo2 } from "lucide-react";

const EMPTY = {
  type: "in_person",
  contact_name: "", contact_phone: "", meeting_point: "",
  self_code: "", self_lockbox_location: "", self_photo: "",
  notes: "",
};

/**
 * Per-booking access override dialog.
 * `booking` — the booking row {id, guest_name, check_in, check_out, access_override?}
 * `propertyDefault` — property.access_instructions (used as fallback preview & reset target)
 * `onSaved(newOverride)` — called after save (newOverride may be null when cleared)
 */
export default function BookingAccessDialog({ booking, propertyDefault, onSaved, trigger }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [access, setAccess] = useState(() => ({ ...EMPTY, ...(booking.access_override || propertyDefault || {}) }));

  useEffect(() => {
    if (open) setAccess({ ...EMPTY, ...(booking.access_override || propertyDefault || {}) });
  }, [open, booking.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasOverride = !!(booking.access_override && booking.access_override.type);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/bookings/${booking.id}/access`, { access_instructions: access });
      toast.success(IT ? "Istruzioni salvate per questa prenotazione" : "Instructions saved for this booking");
      onSaved?.(r.data.access_override);
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.put(`/bookings/${booking.id}/access`, { access_instructions: null });
      toast.success(IT ? "Override rimosso — verrà usato il default della struttura" : "Override cleared — property default will apply");
      onSaved?.(null);
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={`booking-access-dialog-${booking.id}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {IT ? "Check-in per questa prenotazione" : "Check-in for this booking"}
          </DialogTitle>
          <DialogDescription>
            {booking.guest_name} · {booking.check_in} → {booking.check_out}
          </DialogDescription>
        </DialogHeader>

        <div className="text-[11px] text-muted-foreground bg-sand border border-border rounded-md px-3 py-2 mb-2">
          {hasOverride
            ? (IT ? "Questa prenotazione ha un'impostazione personalizzata. Modificala o ripristina il default." : "This booking has a custom setting. Edit or reset to default.")
            : (IT ? "Sta usando il default della struttura. Modifica solo per questa prenotazione." : "Using property default. Change it only for this booking.")}
        </div>

        <RadioGroup value={access.type} onValueChange={(v) => setAccess({ ...access, type: v })} className="grid sm:grid-cols-2 gap-3">
          <label className={`border rounded-lg p-3 cursor-pointer transition-all ${access.type === "in_person" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`} data-testid={`booking-access-type-in_person-${booking.id}`}>
            <div className="flex items-start gap-2">
              <RadioGroupItem value="in_person" id={`ip-${booking.id}`}/>
              <div>
                <div className="font-medium text-sm flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-primary"/>{IT ? "Di persona" : "In-person"}</div>
                <div className="text-[11px] text-muted-foreground">{IT ? "Accoglienza dello staff" : "Staff welcome"}</div>
              </div>
            </div>
          </label>
          <label className={`border rounded-lg p-3 cursor-pointer transition-all ${access.type === "self" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`} data-testid={`booking-access-type-self-${booking.id}`}>
            <div className="flex items-start gap-2">
              <RadioGroupItem value="self" id={`sf-${booking.id}`}/>
              <div>
                <div className="font-medium text-sm flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-primary"/>{IT ? "Self check-in" : "Self check-in"}</div>
                <div className="text-[11px] text-muted-foreground">{IT ? "Codice porta / keybox" : "Door code / keybox"}</div>
              </div>
            </div>
          </label>
        </RadioGroup>

        {access.type === "in_person" ? (
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Nome referente" : "Contact name"}</Label>
              <Input value={access.contact_name || ""} onChange={(e) => setAccess({ ...access, contact_name: e.target.value })} data-testid={`booking-access-contact-name-${booking.id}`}/>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Telefono" : "Phone"}</Label>
              <Input value={access.contact_phone || ""} onChange={(e) => setAccess({ ...access, contact_phone: e.target.value })} placeholder="+39 …" data-testid={`booking-access-contact-phone-${booking.id}`}/>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3"/>{IT ? "Punto d'incontro" : "Meeting point"}</Label>
              <Input value={access.meeting_point || ""} onChange={(e) => setAccess({ ...access, meeting_point: e.target.value })} data-testid={`booking-access-meeting-${booking.id}`}/>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Codice porta / cassaforte" : "Door / safe code"}</Label>
              <Input value={access.self_code || ""} onChange={(e) => setAccess({ ...access, self_code: e.target.value })} placeholder="1234" className="font-mono tracking-widest" data-testid={`booking-access-code-${booking.id}`}/>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Posizione keybox" : "Keybox location"}</Label>
              <Input value={access.self_lockbox_location || ""} onChange={(e) => setAccess({ ...access, self_lockbox_location: e.target.value })} data-testid={`booking-access-keybox-${booking.id}`}/>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Foto (URL)" : "Photo (URL)"}</Label>
              <Input value={access.self_photo || ""} onChange={(e) => setAccess({ ...access, self_photo: e.target.value })} placeholder="https://…" data-testid={`booking-access-photo-${booking.id}`}/>
            </div>
          </div>
        )}

        <div className="mt-3">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Note per il guest" : "Notes for the guest"}</Label>
          <Textarea rows={2} value={access.notes || ""} onChange={(e) => setAccess({ ...access, notes: e.target.value })} data-testid={`booking-access-notes-${booking.id}`}/>
        </div>

        <div className="flex items-center justify-between gap-2 pt-3">
          {hasOverride ? (
            <Button variant="outline" size="sm" onClick={reset} disabled={saving} data-testid={`booking-access-reset-${booking.id}`}>
              <Undo2 className="w-3.5 h-3.5 mr-1"/> {IT ? "Usa default struttura" : "Use property default"}
            </Button>
          ) : <span/>}
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid={`booking-access-save-${booking.id}`}>
            {saving ? "…" : (IT ? "Salva per questa prenotazione" : "Save for this booking")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
